import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiltersWrapper, DateFilter, BooleanFilter, DynamicList } from "../../../../common/components/filters5";
import { CommonFilters, DocumentFilter, icon } from "../../../../common/components";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../../common/providers";
import { ApiService, CommitmentService } from "../../../../api";
import { isNonEmptyArray } from "../../../../common/utils";
import { buildFilterPayload, findDefaultTheme } from "../../utils";
import {
	setFilters,
	setAppliedFilters,
	fetchCommitments,
	setPage,
	updateThemeList,
	setSelectedCommitment,
	setCommitments,
	setTotalCommitments,
} from "../../slice/commitmentTrackingSlice";
import styles from "./CommitmentFilters.module.css";
import { translateFilterLabel, translateFilterOptionLabel } from "../../utils/translate-utils";
import { FIELD_TYPES } from "../../constants";
import { useApi } from "../../../../common/hooks";

const DEFAULT_PAGE = 0;
const DEFAULT_LIMIT = 25;

const FIELD_ICON_MAP = {
	has_penalty: icon.faEuroSign,
	risk: icon.faTriangleExclamation,
	priority: icon.faStar,
	pilot: icon.faUser,
	frequency: icon.faClock,
	completion_deadline: icon.faCalendar,
};

const DEFAULT_VISIBLE_FIELDS = ["risk", "completion_deadline"];

const CommitmentFilters = ({ launchProgress }) => {
	const projectId = useSelector(({ context }) => context?.project?.id);
	const filterDefinitions = useSelector(({ commitmentTracking }) => commitmentTracking.filterDefinitions);
	const filters = useSelector(({ commitmentTracking }) => commitmentTracking.filters);
	const themeFieldUuid = useSelector(({ commitmentTracking }) => commitmentTracking.themeFieldUuid);
	const selectedThemeUuid = useSelector(({ commitmentTracking }) => commitmentTracking.selectedThemeUuid);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const { call: getPilots } = useApi(CommitmentService.getPilots);
	const dispatch = useDispatch();
	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const buildSearchPayload = useCallback(
		(filterState) => buildFilterPayload(filterDefinitions, filterState),
		[filterDefinitions]
	);
	const isThemeField = useCallback((fieldName) => fieldName.toLowerCase() === "theme", []);
	const provideData = useCallback(
		(uuid) =>
			new Promise((resolve) => {
				setTimeout(() => {
					const possibleValues = filterDefinitions?.find(
						(filterDefinition) => filterDefinition.fieldUuid === uuid
					)?.possibleValues;
					let resolveResponse = [...possibleValues];
					if (possibleValues.every((val) => typeof val === "string")) {
						resolveResponse = possibleValues.map((val) => ({ value: val, valueUuuid: val }));
					}
					resolve(
						(isNonEmptyArray(resolveResponse) && { possibleValues: resolveResponse }) || {
							possibleValues: [],
						}
					);
				}, 0);
			}),
		[filterDefinitions]
	);
	const getPilotList = useCallback(async () => {
		try {
			const pilots = await getPilots({ projectId }, { name: "" });
			return {
				possibleValues: pilots.filter((pilot) => pilot).map((pilot) => ({ value: pilot, valueUuid: pilot })),
			};
		} catch (error) {
			console.error("Error fetching field values for pilots :", error);
			return { possibleValues: [] };
		}
	}, [projectId, getPilots]);
	const getFilterRenderer = useCallback(
		(field) => {
			const isDate = field.type === FIELD_TYPES.DATE;
			const isBoolean = field.type === FIELD_TYPES.BOOLEAN;
			const isCategory = field.type === FIELD_TYPES.CATEGORY;
			const isTheme = isThemeField(field.fieldName);
			const isDocument = field.type === FIELD_TYPES.DOCUMENT;
			const isUser = field.type === FIELD_TYPES.USER;
			if (isDate) {
				return <DateFilter type={field.type} />;
			}
			if (isUser) {
				return <DynamicList identifier="valueUuid" labelIdentifier="value" request={() => getPilotList()} />;
			}
			if (isCategory) {
				return (
					<DynamicList
						disabledSearch={!isTheme}
						identifier="valueUuid"
						labelIdentifier="value"
						labelRenderer={!isTheme && ((label) => translateFilterOptionLabel(field.fieldName, label))}
						request={() => provideData(field.fieldUuid)}
					/>
				);
			}
			if (isBoolean) {
				return (
					<BooleanFilter
						falseLabel={translate(
							(field.fieldName === "has_penalty" && "commitment-tracking.filter.has-penalty.without") ||
								"commitment-tracking.filter.no"
						)}
						trueLabel={translate(
							(field.fieldName === "has_penalty" && "commitment-tracking.filter.has-penalty.with") ||
								"commitment-tracking.filter.yes"
						)}
					/>
				);
			}
			if (isDocument) {
				return <DocumentFilter noModeSelection />;
			}
			return null;
		},
		[provideData, isThemeField, getPilotList]
	);
	const getFieldIcon = useCallback(
		(field) => {
			const isTheme = isThemeField(field.fieldName);
			const isDate = field.type === FIELD_TYPES.DATE;
			const isText = field.type === FIELD_TYPES.TEXT;
			if (isTheme) {
				return icon.faSwatchbook;
			}
			if (isDate) {
				return icon.faCalendar;
			}
			if (isText) {
				return icon.faFile;
			}
			return FIELD_ICON_MAP[field.fieldName?.toLowerCase()] || icon.faFilter;
		},
		[isThemeField]
	);
	const isDefaultVisibleField = useCallback((field) => {
		if (field.type === FIELD_TYPES.DATE) {
			return true;
		}
		const fieldNameLower = field.fieldName.toLowerCase();
		return DEFAULT_VISIBLE_FIELDS.some((defaultField) => fieldNameLower === defaultField.toLowerCase());
	}, []);
	const filtersRenderer = useCallback(() => {
		if (!isNonEmptyArray(filterDefinitions)) {
			return [];
		}
		return filterDefinitions
			.filter(
				(field) =>
					field.type !== FIELD_TYPES.TEXT &&
					(isSegFeatureEnabled(SegFlags.DISABLED) ? true : field.fieldName !== "has_penalty")
			)
			.map((field) => {
				const isDate = field.type === FIELD_TYPES.DATE;
				const isDocument = field.type === FIELD_TYPES.DOCUMENT;
				const hasClearSelection =
					(field.type === FIELD_TYPES.CATEGORY && ["theme", "pilot"].includes(field.fieldName)) || isDate;
				const hasIsSelector = ["theme", "pilot", "document"].includes(field.fieldName);
				return {
					default: isDefaultVisibleField(field),
					enabled: true,
					hasIsSeparator: hasIsSelector,
					hasClearSelection,
					hasDateSelector: isDate,
					checkDisableApplicable: isDate
						? (value) => isNonEmptyArray(value) && !value.every((v) => v.value)
						: null,
					component: (isDocument && CommonFilters.DOCUMENTS) || {
						filterKey: field.fieldUuid,
						label: translateFilterLabel(field.fieldName),
						iconName: getFieldIcon(field),
					},
					popUpClassName: isDate ? styles.popup : "",
					renderer: getFilterRenderer(field),
					resizable: isDocument || isDate,
					withDescription: false,
				};
			});
	}, [filterDefinitions, isDefaultVisibleField, getFieldIcon, getFilterRenderer]);
	const handleApply = useCallback(
		async (newFilters) => {
			dispatch(setFilters(newFilters));
			dispatch(setAppliedFilters(newFilters));
			dispatch(setPage(DEFAULT_PAGE));
			dispatch(setSelectedCommitment(null));
			dispatch(setCommitments([]));
			dispatch(setTotalCommitments(0));
			const cancelToken = cancelTokenSourceRef.current?.token;
			const payload = buildSearchPayload(newFilters);
			try {
				let updatedThemes = [];
				if (themeFieldUuid) {
					updatedThemes = await dispatch(
						updateThemeList({
							projectId,
							filters: newFilters,
							themeFieldUuid,
							cancelToken,
						})
					);
				}
				const newThemeId =
					selectedThemeUuid === null && selectedTheme
						? selectedThemeUuid
						: findDefaultTheme(updatedThemes?.payload?.possibleValues || [], selectedThemeUuid);
				await dispatch(
					fetchCommitments({
						projectId,
						payload: {
							...payload,
							theme: { is: true, matchAll: false, elements: newThemeId ? [newThemeId] : [] },
						},
						page: DEFAULT_PAGE,
						limit: DEFAULT_LIMIT,
						cancelToken,
					})
				).unwrap();
			} catch (error) {
				console.error("Error applying filters:", error);
			}
		},
		[dispatch, projectId, themeFieldUuid, buildSearchPayload, selectedThemeUuid, selectedTheme]
	);
	const handleRemove = useCallback(
		(newFilters) => {
			handleApply(newFilters);
		},
		[handleApply]
	);
	const customLabelApply = useCallback((results) => {
		if (results === null) {
			return translate("commitment-tracking.filters.apply");
		}
		if (results <= 1) {
			return translate("commitment-tracking.filters.result", { results });
		}
		return translate("commitment-tracking.filters.results", { results });
	}, []);
	if (!isNonEmptyArray(filterDefinitions)) {
		return null;
	}
	return (
		<div className={styles.container}>
			<FiltersWrapper
				hasSearch
				hasSearchVerticalBar
				className={styles.filtersWrapper}
				components={filtersRenderer()}
				customLabelApply={customLabelApply}
				defaultFilters={filters}
				isGloballyDisabled={launchProgress}
				searchPlaceHolder={translate("commitment-tracking.filters.search-placeholder")}
				onApply={handleApply}
				onRemove={handleRemove}
			/>
		</div>
	);
};

export default CommitmentFilters;
